/**
 * card-swipe.js — Sam's Store UAE v5
 * Product card image carousel
 * Mobile: touch swipe + dots
 * Desktop: premium arrows + dots
 *
 * Performance:
 * - Reuses Dawn's already-rendered primary image (no duplicate request).
 * - Preloads adjacent images when a card nears the viewport or receives intent.
 * - Keeps the current image visible until the requested image is decoded.
 */

(function () {
    'use strict';

    var SWIPE_THRESHOLD = 35;
    var PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

    function canShowDesktopArrows() {
        if (!window.matchMedia) return window.innerWidth >= 750;
        return window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;
    }

    function usesTouchCarousel() {
        if (!window.matchMedia) return window.innerWidth < 750;
        return window.matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)').matches;
    }

    function getData(el) {
        try {
            return JSON.parse(el.dataset.images || '[]');
        } catch (error) {
            return [];
        }
    }

    function buildImg(url) {
        var img = document.createElement('img');
        img.className = 'card-swipe__img';
        img.src = PLACEHOLDER;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.draggable = false;
        img.dataset.lazySrc = url;
        return img;
    }

    function ensureReady(img) {
        if (!img) return Promise.resolve(null);
        if (img._samReadyPromise) return img._samReadyPromise;

        var lazySrc = img.dataset.lazySrc;
        if (lazySrc) {
            img.loading = 'eager';
            img.src = lazySrc;
            delete img.dataset.lazySrc;
        }

        img._samReadyPromise = new Promise(function (resolve) {
            if (img.complete) {
                resolve();
                return;
            }

            function finish() {
                img.removeEventListener('load', finish);
                img.removeEventListener('error', finish);
                resolve();
            }

            img.addEventListener('load', finish, { once: true });
            img.addEventListener('error', finish, { once: true });
        }).then(function () {
            if (img.decode && img.naturalWidth > 0) {
                return img.decode().catch(function () {});
            }
        }).then(function () {
            return img;
        });

        return img._samReadyPromise;
    }

    function requestedIndex(track) {
        if (track.dataset.pending !== undefined) {
            return Number(track.dataset.pending);
        }
        return Number(track.dataset.current || 0);
    }

    function warmAdjacent(track, idx) {
        var slides = track.querySelectorAll('.card-swipe__img');
        if (slides.length < 2) return;

        var next = (idx + 1) % slides.length;
        ensureReady(slides[next]);

        if (slides.length > 2) {
            var previous = (idx - 1 + slides.length) % slides.length;
            ensureReady(slides[previous]);
        }
    }

    function setActiveSlide(track, container, idx) {
        var slides = track.querySelectorAll('.card-swipe__img');
        var dots = container.querySelectorAll('.card-swipe__dot');

        Array.prototype.forEach.call(slides, function (slide, slideIndex) {
            slide.classList.toggle('is-active', slideIndex === idx);
        });

        Array.prototype.forEach.call(dots, function (dot, dotIndex) {
            var active = dotIndex === idx;
            dot.classList.toggle('is-active', active);
            if (active) {
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.removeAttribute('aria-current');
            }
        });

        track.dataset.current = String(idx);
        delete track.dataset.pending;
        container.classList.remove('is-card-swipe-loading');
        warmAdjacent(track, idx);
    }

    function goTo(track, container, idx) {
        var slides = track.querySelectorAll('.card-swipe__img');
        if (!slides.length) return;

        idx = ((idx % slides.length) + slides.length) % slides.length;

        if (idx === Number(track.dataset.current || 0) && track.dataset.pending === undefined) {
            return;
        }

        track.dataset.pending = String(idx);
        track._samNavigationToken = (track._samNavigationToken || 0) + 1;
        var navigationToken = track._samNavigationToken;
        var target = slides[idx];

        if (!target.complete || target.dataset.lazySrc) {
            container.classList.add('is-card-swipe-loading');
        }

        ensureReady(target).then(function () {
            if (navigationToken !== track._samNavigationToken) return;

            if (!target.naturalWidth) {
                delete track.dataset.pending;
                container.classList.remove('is-card-swipe-loading');
                return;
            }

            setActiveSlide(track, container, idx);
        });
    }

    function buildDots(count, track, container) {
        var wrap = document.createElement('div');
        wrap.className = 'card-swipe__dots';
        wrap.setAttribute('aria-label', 'Product images');

        for (var i = 0; i < count; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'card-swipe__dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Show image ' + (i + 1));
            if (i === 0) dot.setAttribute('aria-current', 'true');

            (function (index, button) {
                button.addEventListener('pointerenter', function () {
                    ensureReady(track.querySelectorAll('.card-swipe__img')[index]);
                });

                button.addEventListener('focus', function () {
                    ensureReady(track.querySelectorAll('.card-swipe__img')[index]);
                });

                button.addEventListener('click', function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    goTo(track, container, index);
                });
            })(i, dot);

            wrap.appendChild(dot);
        }

        return wrap;
    }

    function buildArrow(direction, track, container) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'card-swipe__arrow card-swipe__arrow--' + direction;
        button.setAttribute('aria-label', direction === 'prev' ? 'Previous product image' : 'Next product image');
        button.innerHTML = direction === 'prev'
            ? '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 15.5 7 10l5.5-5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m7.5 4.5 5.5 5.5-5.5 5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        function destination() {
            var current = requestedIndex(track);
            var total = Number(track.dataset.total || 1);
            return direction === 'prev'
                ? (current - 1 + total) % total
                : (current + 1) % total;
        }

        function warmDestination() {
            var slides = track.querySelectorAll('.card-swipe__img');
            ensureReady(slides[destination()]);
        }

        button.addEventListener('pointerenter', warmDestination);
        button.addEventListener('focus', warmDestination);
        button.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            goTo(track, container, destination());
        });

        return button;
    }

    function addTouchSwipe(track, container) {
        var startX = 0;
        var startY = 0;
        var moved = false;

        track.addEventListener('touchstart', function (event) {
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            moved = false;
            warmAdjacent(track, requestedIndex(track));
        }, { passive: true });

        track.addEventListener('touchmove', function () {
            moved = true;
        }, { passive: true });

        track.addEventListener('touchend', function (event) {
            if (!moved) return;

            var deltaX = event.changedTouches[0].clientX - startX;
            var deltaY = event.changedTouches[0].clientY - startY;
            if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;

            var current = requestedIndex(track);
            var total = Number(track.dataset.total || 1);
            track._samSuppressNavigation = true;
            window.setTimeout(function () {
                track._samSuppressNavigation = false;
            }, 450);
            goTo(track, container, deltaX < 0 ? (current + 1) % total : (current - 1 + total) % total);
        }, { passive: true });
    }

    function addMobileImageNavigation(track, mediaDiv) {
        track.addEventListener('click', function (event) {
            if (!usesTouchCarousel()) return;

            event.preventDefault();
            event.stopPropagation();

            if (track._samSuppressNavigation) {
                track._samSuppressNavigation = false;
                return;
            }

            var card = mediaDiv.closest('.card');
            var productLink = card && card.querySelector('.card__heading a[href]');
            if (productLink) window.location.assign(productLink.href);
        });
    }

    function addIntentPreload(mediaDiv, track) {
        var warmed = false;

        function warm() {
            if (warmed) return;
            warmed = true;
            warmAdjacent(track, 0);
        }

        mediaDiv.addEventListener('mouseenter', warm, { once: true });
        mediaDiv.addEventListener('focusin', warm, { once: true });
        mediaDiv.addEventListener('touchstart', warm, { once: true, passive: true });

        if ('IntersectionObserver' in window) {
            var observer = new IntersectionObserver(function (entries) {
                if (!entries[0].isIntersecting) return;
                warm();
                observer.disconnect();
            }, { rootMargin: '650px 0px' });

            observer.observe(mediaDiv);
        } else {
            window.setTimeout(warm, 250);
        }
    }

    function enhance(mediaDiv) {
        if (mediaDiv.dataset.swiped) return;
        mediaDiv.dataset.swiped = '1';

        var images = getData(mediaDiv);
        if (!images || images.length < 2) return;

        var innerMedia = mediaDiv.querySelector('.media');
        if (!innerMedia) return;

        var cardInner = mediaDiv.closest('.card__inner');
        if (cardInner) cardInner.classList.add('has-card-swipe');

        var originalImages = innerMedia.querySelectorAll(':scope > img');
        var primaryImage = originalImages[0];
        if (!primaryImage) return;

        var showArrows = document.documentElement.dataset.cardSwipeArrows !== 'false';
        var track = document.createElement('div');
        track.className = 'card-swipe__track';
        track.dataset.current = '0';
        track.dataset.total = String(images.length);

        primaryImage.classList.add('card-swipe__img', 'is-active');
        primaryImage.style.display = '';
        primaryImage.removeAttribute('aria-hidden');
        primaryImage.draggable = false;
        track.appendChild(primaryImage);

        images.slice(1).forEach(function (url) {
            track.appendChild(buildImg(url));
        });

        Array.prototype.slice.call(originalImages, 1).forEach(function (image) {
            image.style.display = 'none';
            image.setAttribute('aria-hidden', 'true');
        });

        innerMedia.appendChild(track);
        innerMedia.appendChild(buildDots(images.length, track, innerMedia));

        if (showArrows && canShowDesktopArrows()) {
            innerMedia.appendChild(buildArrow('prev', track, innerMedia));
            innerMedia.appendChild(buildArrow('next', track, innerMedia));
        }

        addTouchSwipe(track, innerMedia);
        addMobileImageNavigation(track, mediaDiv);
        addIntentPreload(mediaDiv, track);
    }

    function scanCards() {
        if (document.documentElement.dataset.cardSwipeEnabled === 'false') return;
        document.querySelectorAll('.card__media[data-images]').forEach(enhance);
    }

    function init() {
        scanCards();

        if (!window.MutationObserver) return;

        new MutationObserver(function (mutations) {
            var needsScan = mutations.some(function (mutation) {
                return Array.prototype.some.call(mutation.addedNodes, function (node) {
                    if (node.nodeType !== 1) return false;
                    return (node.matches && node.matches('.card__media[data-images]'))
                        || (node.querySelector && node.querySelector('.card__media[data-images]'));
                });
            });

            if (needsScan) window.setTimeout(scanCards, 80);
        }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();